(ns app.front.stamp.render
  "Compose editable stamp layouts into PNG snapshots in the browser. Layout geometry
   is in PDF points; final output and editor crops share the same drawing code."
  (:require [app.domain.stamp.model :as stamp-model]
            [clojure.string :as str]
            [core.front.date-util :as date-util]))

(defonce script-font (atom nil))

(defn load-font! []
  (or @script-font
      (let [pending (-> (.load js/document.fonts "24px StampSignature")
                        (.then (fn [fonts]
                                 (or (first fonts)
                                     (throw (js/Error. "Stamp signature font unavailable")))))
                        (.catch (fn [error] (reset! script-font nil) (throw error))))]
        (reset! script-font pending)
        pending)))

(defn- load-image! [url signal]
  (-> (js/fetch url #js {:credentials "same-origin" :signal signal})
      (.then (fn [response]
               (when-not (.-ok response) (throw (js/Error. "Stamp image unavailable")))
               (.blob response)))
      (.then js/createImageBitmap)))

(defn- horizontal-text-bounds [metrics]
  {:left  (reduce max 0 (map #(.-actualBoundingBoxLeft %) metrics))
   :right (reduce max 0 (map #(max (.-width %) (.-actualBoundingBoxRight %)) metrics))})

(defn text-metrics [{:keys [text font-size]}]
  (let [context (.getContext (.createElement js/document "canvas") "2d")
        ;; Unlike split-lines, a negative limit retains trailing empty lines.
        lines   (str/split (str/replace (or text "") "\t" "    ") #"\r\n|\r|\n" -1)]
    (set! (.-font context) (str font-size "px sans-serif"))
    (let [metrics              (mapv #(.measureText context %) lines)
          {:keys [left right]} (horizontal-text-bounds metrics)
          ascent               (reduce max font-size (map #(.-actualBoundingBoxAscent %) metrics))
          descent              (reduce max (* font-size 0.25) (map #(.-actualBoundingBoxDescent %) metrics))
          line-height          (+ ascent descent)]
      {:lines       lines
       :left        (+ 1 left)
       :baseline    (+ 1 ascent)
       :line-height line-height
       :width       (max 0.72 (+ 2 left right))
       :height      (+ 2 (* (count lines) line-height))})))

(defn measured-element [element]
  (if (#{:text :date} (:type element))
    (merge element
           (select-keys (text-metrics (if (= :date (:type element))
                                        (assoc element :text (-> "2000-12-28"
                                                                 date-util/parse-local-date
                                                                 date-util/date))
                                        element))
                        [:width :height]))
    element))

(defn measured-layout [layout]
  (update layout :elements #(mapv measured-element %)))

(defn- draw-free-text! [context {:keys [font-size color] :as element}]
  (let [{:keys [lines left baseline line-height]} (text-metrics element)]
    (set! (.-fillStyle context) color)
    (set! (.-font context) (str font-size "px sans-serif"))
    (set! (.-textBaseline context) "alphabetic")
    (doseq [[i line] (map-indexed vector lines)]
      (.fillText context line left (+ baseline (* i line-height))))))

(defn- draw-signature! [context {:keys [width height font-size color]} {:keys [full-name]}]
  (let [family "StampSignature, sans-serif"
        lines  (str/split-lines (or full-name ""))]
    (set! (.-fillStyle context) color)
    (set! (.-font context) (str font-size "px " family))
    (let [metrics              (mapv #(.measureText context %) lines)
          {:keys [left right]} (horizontal-text-bounds metrics)
          scale                (min (/ width (+ left (max 1 right)))
                                    (/ height (* font-size 1.25 (max 1 (count lines)))))
          size                 (* font-size scale)]
      (set! (.-font context) (str size "px " family))
      (set! (.-textBaseline context) "middle")
      (doseq [[i line] (map-indexed vector lines)]
        (.fillText context line (* left scale)
                   (+ (/ height 2) (* (- i (/ (dec (count lines)) 2)) size 1.25)))))))

(defn- draw-element! [context {:keys [x y width height rotation type] :as element} snapshot image]
  (.save context)
  (try
    (.translate context (+ x (/ width 2)) (+ y (/ height 2)))
    (.rotate context (* rotation (/ js/Math.PI 180)))
    (.translate context (- (/ width 2)) (- (/ height 2)))
    (.beginPath context)
    (.rect context 0 0 width height)
    (.clip context)
    (cond
      (= type :image)
      (let [scale (min (/ width (.-width image)) (/ height (.-height image)))
            w     (* scale (.-width image))
            h     (* scale (.-height image))]
        (.drawImage context image (/ (- width w) 2) (/ (- height h) 2) w h))
      (#{:text :date} type)
      (draw-free-text! context (if (= type :date)
                                 (assoc element :text (-> (:date snapshot)
                                                          date-util/parse-local-date
                                                          date-util/date))
                                 element))

      :else
      (draw-signature! context element snapshot))
    (finally (.restore context))))

(defn- check-cancelled!
  "Stop obsolete rendering work between async steps by throwing AbortError when cancelled."
  [signal]
  (when (and signal (.-aborted signal))
    (throw (js/DOMException. "Cancelled" "AbortError"))))

(defn- draw-sized!
  [{:keys [layout image-urls viewport signal] :as snapshot}
   [pixels-w pixels-h] canvas load! release!]
  (if-not (stamp-model/valid-layout? layout)
    (js/Promise.reject (js/Error. "Invalid stamp layout"))
    (let [{:keys [x y width height] :or {x 0 y 0}} (or viewport layout)
          context                                  (.getContext canvas "2d")]
      (set! (.-width canvas) pixels-w)
      (set! (.-height canvas) pixels-h)
      (.scale context (/ pixels-w width) (/ pixels-h height))
      (.translate context (- x) (- y))
      (set! (.-imageSmoothingQuality context) "high")
      ;; Sequential decoding bounds peak asset memory independently of element count.
      (-> (reduce (fn [pending element]
                    (.then pending
                           (fn []
                             (check-cancelled! signal)
                             (if (= :image (:type element))
                               (-> (load! (get image-urls (:asset element)))
                                   (.then (fn [image]
                                            (try
                                              (check-cancelled! signal)
                                              (draw-element! context element snapshot image)
                                              (finally (release! image))))))
                               (draw-element! context element snapshot nil)))))
                  (if (stamp-model/has-type? layout :signature) (load-font!) (js/Promise.resolve nil))
                  (map measured-element (:elements layout)))
          (.then (fn []
                   (check-cancelled! signal)
                   (when-let [color (:border layout)]
                     ;; Inset bands retain the physical width even on canvases narrower than the border.
                     (let [w  (:width layout)
                           h  (:height layout)
                           dx (min w (stamp-model/border-width layout))
                           dy (min h (stamp-model/border-width layout))]
                       (set! (.-fillStyle context) color)
                       (.fillRect context 0 0 w dy)
                       (.fillRect context 0 (- h dy) w dy)
                       (.fillRect context 0 0 dx h)
                       (.fillRect context (- w dx) 0 dx h)))
                   canvas))))))

(defn compose-sized! [snapshot dimensions]
  (-> (draw-sized! snapshot dimensions (.createElement js/document "canvas")
                   #(load-image! % (:signal snapshot)) #(.close %))
      (.then (fn [canvas]
               (js/Promise.
                 (fn [resolve reject]
                   (.toBlob canvas #(if % (resolve %) (reject (js/Error. "Stamp rendering failed")))
                            "image/png")))))))

(defn- load-editor-image! [url signal]
  (let [image  (js/Image.)
        abort! #(.removeAttribute image "src")]
    (set! (.-src image) url)
    (.addEventListener signal "abort" abort! #js {:once true})
    (-> (.decode image)
        (.then (fn []
                 (check-cancelled! signal)
                 image))
        (.finally #(.removeEventListener signal "abort" abort!)))))

(defn- release-editor-image! [image]
  (if (.-close image)
    (.close image)
    (.removeAttribute image "src")))

(defn editor-renderer
  "Reuse a staging canvas and loaded image elements for the active editor assets.
   The browser manages decoded image memory and HTTP caching. Obsolete frames do not
   cancel shared image preparation; removing an asset or disposing the editor does."
  ([] (editor-renderer {}))
  ([{:keys [load-image-fn] :or {load-image-fn load-editor-image!}}]
   (let [canvas     (.createElement js/document "canvas")
         controller (js/AbortController.)
         *images    (atom {})
         remove!    (fn [url]
                      (when-let [{:keys [image controller]} (get @*images url)]
                        (swap! *images dissoc url)
                        (.abort controller)
                        (when image (release-editor-image! image))))
         load!      (fn [url]
                      (or (:promise (get @*images url))
                          (let [controller (js/AbortController.)
                                pending    (-> (js/Promise.resolve)
                                               (.then #(do (check-cancelled! (.-signal controller))
                                                           (load-image-fn url (.-signal controller))))
                                               (.then (fn [image]
                                                        (when (.-aborted (.-signal controller))
                                                          (release-editor-image! image)
                                                          (check-cancelled! (.-signal controller)))
                                                        (swap! *images assoc-in [url :image] image)
                                                        image))
                                               (.catch (fn [error]
                                                         (when (identical? controller (:controller (get @*images url)))
                                                           (remove! url))
                                                         (throw error))))]
                            (swap! *images assoc url {:controller controller :promise pending})
                            pending)))]
     {:draw!    (fn [{:keys [viewport pixel-scale signal layout image-urls] :as snapshot}]
                  (let [urls (-> image-urls
                                 (select-keys (stamp-model/image-keys layout))
                                 vals
                                 set)]
                    (doseq [url (keys @*images) :when (not (contains? urls url))]
                      (remove! url)))
                  (let [signal (if signal
                                 (js/AbortSignal.any #js [signal (.-signal controller)])
                                 (.-signal controller))]
                    (draw-sized! (assoc snapshot :signal signal)
                                 (mapv #(max 1 (js/Math.round (* pixel-scale (% viewport)))) [:width :height])
                                 canvas load! (constantly nil))))
      :dispose! (fn []
                  (.abort controller)
                  (doseq [url (keys @*images)] (remove! url)))})))

(defn compose!
  "Returns a self-contained PNG Blob. Call once at selection, then armStamp with these
   bytes: repeated placements intentionally retain the same date, name and design."
  [{:keys [layout viewport] :as snapshot}]
  (let [{:keys [width height]} (or viewport layout)
        pixels-per-point       (/ 300 stamp-model/points-per-inch)]
    (compose-sized! snapshot (stamp-model/output-dimensions (* width pixels-per-point)
                                                            (* height pixels-per-point)))))
